/** Thành tích (`S.rewards`, spec 16) và Game giờ nghỉ (`S.game`, spec 17) của bản mẫu. */
import { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { POINT_RULES, PointEntry } from "@ca/shared";
import { Button, Card, Chip, Fish, Kpi, Modal, PageHead, SpaceTag } from "@ca/ui";
import { api } from "./api.ts";
import { focusLeft, startBreak, useFocusClock } from "./focus.tsx";
import { useAchievements } from "./side-counts.ts";
import { NewBadge } from "./flags.tsx";
import { mmss } from "./charts.tsx";

/** Bình bong bóng (`jarSVG`): mực nước theo số bong bóng, đầy ở 300. */
function Jar({ n }: { n: number }) {
  const lvl = Math.min(1, n / 300),
    top = 40 + (1 - lvl) * 150;
  const body =
    "M40 40 h100 a12 12 0 0 1 12 12 v126 a24 24 0 0 1 -24 24 h-76 a24 24 0 0 1 -24 -24 v-126 a12 12 0 0 1 12 -12z";
  return (
    <svg viewBox="0 0 180 210" aria-label="Bình bong bóng">
      <defs>
        <clipPath id="jarclip">
          <path d={body} />
        </clipPath>
      </defs>
      <rect x="52" y="14" width="76" height="22" rx="8" style={{ fill: "var(--ink)" }} />
      <path
        d={body}
        style={{ fill: "var(--surface-2)", stroke: "var(--line-2)" }}
        strokeWidth="3"
      />
      <g clipPath="url(#jarclip)">
        <rect
          x="20"
          y={top}
          width="150"
          height="200"
          style={{ fill: "var(--accent-2)" }}
          opacity=".85"
        />
        {n > 0 &&
          Array.from({ length: 14 }, (_, i) => (
            <circle
              key={i}
              cx={40 + ((i * 37) % 100)}
              cy={Math.max(top + 8, 60 + ((i * 53) % 130))}
              r={4 + (i % 4) * 2}
              fill="none"
              stroke="#fff"
              strokeOpacity=".55"
              strokeWidth="1.5"
            />
          ))}
      </g>
      <path
        d="M58 60 v100"
        stroke="#fff"
        strokeOpacity=".5"
        strokeWidth="6"
        strokeLinecap="round"
      />
    </svg>
  );
}
const BG_ICON: Record<string, string> = { forest: "🌲", night: "🌌" };
const POINT_LABEL: Record<string, string> = {
  ...Object.fromEntries(POINT_RULES.map((r) => [r.reason, r.label])),
  attempt: "Làm đúng câu trong bài",
  redeem: "Đổi bong bóng lấy quà",
};
export function RewardsPage({ uid, notify }: { uid: string; notify: (s: string) => void }) {
  const q = useAchievements(uid),
    history = useQuery({
      queryKey: ["points", uid],
      queryFn: async () =>
        z.array(PointEntry).parse(await api("/v1/me/points?limit=5", { accountId: uid })),
    }),
    cache = useQueryClient(),
    [confirm, setConfirm] = useState<{ key: string; name: string; cost: number } | null>(null),
    [busy, setBusy] = useState(false);
  const n = q.data?.points ?? 0,
    rounds = q.data?.rounds ?? 0,
    minutes = q.data?.minutes ?? 0,
    days = q.data?.days ?? 0,
    fixes = q.data?.fixes ?? 0,
    earned = q.data?.earned ?? [];
  const next = (q.data?.items ?? []).filter((i) => !i.owned).sort((a, b) => a.cost - b.cost)[0];
  const badges: [string, string, string, string][] = [
    ["first_focus", "🍅", "Hiệp đầu tiên", rounds >= 1 ? "đã đạt" : "xong 1 hiệp tập trung"],
    ["ten_focus", "⏱️", "Mười hiệp", rounds >= 10 ? "đã đạt" : `${rounds}/10 hiệp`],
    ["first_hour", "📚", "Một giờ học", minutes >= 60 ? "đã đạt" : `còn ${60 - minutes} phút`],
    ["seven_days", "🗓️", "Bảy ngày học", days >= 7 ? "đã đạt" : `${days}/7 ngày`],
    ["five_fixes", "📕", "Trị 5 nhóm lỗi", fixes >= 5 ? "đã đạt" : `${fixes}/5 nhóm`],
  ];
  return (
    <>
      <PageHead
        space="prod"
        title={
          <>
            Thành tích <NewBadge />
          </>
        }
        desc="Xem cách kiếm bong bóng và đổi bong bóng lấy hình nền cho chế độ tập trung."
      />
      <div className="grid g-7-5">
        <Card>
          <div className="row" style={{ gap: 20, alignItems: "center" }}>
            <div className="jarbig">
              <Jar n={n} />
            </div>
            <div className="stack" style={{ gap: 6 }}>
              <span className="eyebrow">Bình bong bóng của bạn</span>
              <b style={{ fontSize: 46, letterSpacing: "-.03em", lineHeight: 1 }}>{n} 🫧</b>
              <p className="muted">
                {n === 0 ? (
                  <>Xong 1 hiệp tập trung để nhận 10 🫧.</>
                ) : next ? (
                  <>
                    Còn <b>{Math.max(0, next.cost - n)}</b> 🫧 để mở hình nền “{next.name}”.
                  </>
                ) : (
                  <>Bạn đã mở hết hình nền hiện có.</>
                )}
              </p>
            </div>
          </div>
        </Card>
        <Card>
          <h3 style={{ marginBottom: 6 }}>Cách nhận bong bóng</h3>
          {POINT_RULES.map((r) => (
            <div key={r.reason} className="list-row">
              <span style={{ fontSize: 18 }}>{r.icon}</span>
              <span className="grow">
                {r.label} {r.isNew && <NewBadge />}
              </span>
              <b className="num">+{r.points}</b>
            </div>
          ))}
          <p className="faint" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 8 }}>
            {rounds} hiệp · {minutes} phút học · {days} ngày có học
          </p>
          {!!history.data?.length && (
            <>
              <div className="eyebrow" style={{ margin: "14px 0 4px" }}>
                Gần đây
              </div>
              {history.data.map((h, i) => (
                <div key={i} className="list-row">
                  <span className="faint num" style={{ fontSize: 12.5, fontWeight: 700 }}>
                    {new Date(h.createdAt).toLocaleDateString("vi-VN", {
                      day: "2-digit",
                      month: "2-digit",
                    })}
                  </span>
                  <span className="grow">{POINT_LABEL[h.reason] ?? h.reason}</span>
                  <b className="num">
                    {h.delta > 0 ? "+" : ""}
                    {h.delta}
                  </b>
                </div>
              ))}
            </>
          )}
        </Card>
      </div>
      <Card>
        <div className="card-head">
          <div>
            <h2>Đổi bong bóng</h2>
            <p className="sub">Cho bong bóng một công dụng, để phần thưởng có ý nghĩa</p>
          </div>
        </div>
        <div className="grid g4">
          <div className="badge">
            <span className="bi">🌊</span>
            <b>Hình nền “Biển”</b>
            <small>có sẵn</small>
          </div>
          {q.data?.items.map((item) => {
            const locked = !item.owned && item.cost > n;
            return (
              <button
                key={item.key}
                className={`badge${locked ? " locked" : ""}`}
                disabled={item.owned || locked}
                onClick={() => {
                  setConfirm(item);
                }}
              >
                <span className="bi">{BG_ICON[item.key] ?? "🖼"}</span>
                <b>Hình nền “{item.name}”</b>
                <small>
                  {item.owned
                    ? "đã sở hữu"
                    : locked
                      ? `${item.cost} 🫧`
                      : `đổi được · ${item.cost} 🫧`}
                </small>
              </button>
            );
          })}
        </div>
      </Card>
      <Card>
        <div className="card-head">
          <h2>Huy hiệu</h2>
        </div>
        <div className="grid g4">
          {badges.map(([key, icon, name, sub]) => (
            <div key={key} className={`badge${earned.includes(key) ? "" : " locked"}`}>
              <span className="bi">{icon}</span>
              <b>{name}</b>
              <small>{sub}</small>
            </div>
          ))}
        </div>
      </Card>
      {q.isError && <p role="alert">{String(q.error)}</p>}
      {confirm && (
        <Modal
          title="Đổi bong bóng"
          className="sm"
          head={<SpaceTag space="prod" />}
          onClose={() => {
            setConfirm(null);
          }}
        >
          <h2>
            Đổi {confirm.cost} 🫧 lấy hình nền “{confirm.name}”?
          </h2>
          <p className="muted" style={{ fontSize: 13.5, marginTop: 6 }}>
            Hình nền dùng cho màn tập trung toàn màn hình; chọn ở trang Tập trung.
          </p>
          <div className="row" style={{ justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
            <Button
              variant="soft"
              onClick={() => {
                setConfirm(null);
              }}
            >
              Để sau
            </Button>
            <Button
              variant="acc"
              disabled={busy}
              onClick={() => {
                setBusy(true);
                void api(`/v1/me/rewards/${confirm.key}`, { accountId: uid, method: "PUT" })
                  .then(async () => {
                    setConfirm(null);
                    notify(`Đã mở hình nền “${confirm.name}”.`);
                    await cache.invalidateQueries({ queryKey: ["achievements", uid] });
                    await cache.invalidateQueries({ queryKey: ["points", uid] });
                  })
                  .catch((err: unknown) => {
                    notify(String(err));
                  })
                  .finally(() => {
                    setBusy(false);
                  });
              }}
            >
              Đổi hình nền
            </Button>
          </div>
        </Modal>
      )}
    </>
  );
}

/* Số lần chơi và kỷ lục chỉ lưu trên máy này (tiện lợi, không phải dữ liệu học). */
function gameStore() {
  const day = new Date().toISOString().slice(0, 10);
  try {
    const raw = JSON.parse(localStorage.getItem("ca-game") ?? "{}") as {
      day?: string;
      plays?: number;
      secs?: number;
      best?: number;
    };
    return {
      plays: raw.day === day ? (raw.plays ?? 0) : 0,
      secs: raw.day === day ? (raw.secs ?? 0) : 0,
      best: raw.best ?? 0,
      day,
    };
  } catch {
    return { plays: 0, secs: 0, best: 0, day };
  }
}
function saveGame(v: ReturnType<typeof gameStore>) {
  try {
    localStorage.setItem("ca-game", JSON.stringify(v));
  } catch {
    /* trình duyệt chặn lưu trữ */
  }
}
export function BreakGame({ uid, notify }: { uid: string; notify: (s: string) => void }) {
  const q = useFocusClock(uid),
    run = q.data?.run,
    resting = !!run?.completedAt && q.now < Date.parse(run.breakEndsAt ?? ""),
    focusing = !!run && !run.completedAt && !run.cancelledAt,
    allowed = q.isFetchedAfterMount && !q.isError && navigator.onLine && resting,
    left = focusLeft(run, q.now),
    canvas = useRef<HTMLCanvasElement>(null),
    [playing, setPlaying] = useState(false),
    [score, setScore] = useState(0),
    [stats, setStats] = useState(gameStore);
  const rest = () => {
    void startBreak(uid).catch((e: unknown) => {
      notify(String(e));
    });
  };
  useEffect(() => {
    if (!allowed) setPlaying(false);
  }, [allowed]);
  useEffect(() => {
    if (!playing || !allowed || !canvas.current) return;
    const node = canvas.current,
      ctx = node.getContext("2d");
    if (!ctx) return;
    setScore(0);
    const started = Date.now();
    let frame = 0,
      last = performance.now(),
      popped = 0;
    const bubbles = Array.from({ length: 18 }, () => ({
      x: Math.random() * 640 + 30,
      y: Math.random() * 400 + 10,
      r: Math.random() * 12 + 14,
      speed: Math.random() * 20 + 10,
    }));
    const draw = (t: number) => {
      const delta = Math.min(0.05, (t - last) / 1000);
      last = t;
      ctx.clearRect(0, 0, 700, 420);
      for (const b of bubbles) {
        b.y -= b.speed * delta;
        if (b.y < -b.r) {
          b.y = 440;
          b.x = Math.random() * 640 + 30;
        }
        ctx.beginPath();
        ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
        ctx.fillStyle = "#fff2";
        ctx.fill();
        ctx.strokeStyle = "#b8f1ff";
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(b.x - b.r / 3, b.y - b.r / 3, b.r / 5, 0, Math.PI * 2);
        ctx.fillStyle = "#fff9";
        ctx.fill();
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    const pop = (e: PointerEvent) => {
      const box = node.getBoundingClientRect(),
        x = ((e.clientX - box.left) / box.width) * 700,
        y = ((e.clientY - box.top) / box.height) * 420;
      const b = bubbles.find((b) => Math.hypot(x - b.x, y - b.y) <= b.r);
      if (b) {
        b.y = 440;
        popped++;
        setScore((s) => s + 1);
      }
    };
    node.addEventListener("pointerdown", pop);
    return () => {
      cancelAnimationFrame(frame);
      node.removeEventListener("pointerdown", pop);
      const s = gameStore();
      const nextStats = {
        ...s,
        plays: s.plays + 1,
        secs: s.secs + Math.round((Date.now() - started) / 1000),
        best: Math.max(s.best, popped),
      };
      saveGame(nextStats);
      setStats(nextStats);
    };
  }, [playing, allowed, run?.id]);
  return (
    <>
      <PageHead
        space="prod"
        title={
          <>
            Game giờ nghỉ <NewBadge />
          </>
        }
        desc="Chơi trong giờ nghỉ của Pomodoro để đầu óc thả lỏng mà không phải cầm điện thoại lên. Giờ chơi không tính vào giờ học; hết giờ nghỉ, game tự khoá."
      />
      {allowed ? (
        <div className="gstatus on">
          <span className="gs-ic">☕</span>
          <div className="grow">
            <b>
              Đang giờ nghỉ · còn <span className="num">{mmss(left)}</span>
            </b>
            <p>Chơi thoải mái. Hết giờ nghỉ, game tự khoá và nhắc bạn quay lại bài.</p>
          </div>
        </div>
      ) : focusing ? (
        <div className="gstatus">
          <span className="gs-ic">🍅</span>
          <div className="grow">
            <b>
              Đang trong hiệp tập trung · còn <span className="num">{mmss(left)}</span>
            </b>
            <p>Game mở khi tới giờ nghỉ. Muốn nghỉ sớm thì bấm bên cạnh, hiệp này sẽ dừng.</p>
          </div>
          <Button variant="soft" size="sm" onClick={rest}>
            ☕ Nghỉ sớm 5 phút
          </Button>
        </div>
      ) : (
        <div className="gstatus">
          <span className="gs-ic">⏱</span>
          <div className="grow">
            <h2 style={{ fontSize: 15 }}>Game dành cho giờ nghỉ</h2>
            <p>
              Chưa có hiệp tập trung nào đang chạy. Bạn có thể nghỉ 5 phút ngay; hết 5 phút game tự
              khoá.
            </p>
          </div>
          <Button variant="dark" size="sm" onClick={rest}>
            ☕ Nghỉ 5 phút & chơi
          </Button>
        </div>
      )}
      <div className="grid g2">
        <article className="gcard">
          {playing && allowed ? (
            <div className="gthumb gt-bubble" style={{ height: "auto" }}>
              <canvas
                ref={canvas}
                width={700}
                height={420}
                style={{ width: "100%", height: "auto", display: "block" }}
                aria-label="Bể bong bóng, bấm vào các bong bóng"
              />
            </div>
          ) : (
            <div className="gthumb gt-bubble" aria-hidden="true">
              {[
                [14, 18, -1],
                [34, 30, -3],
                [62, 14, -2],
                [80, 24, -4.5],
              ].map(([l, s, d]) => (
                <i
                  key={l}
                  style={{ left: `${l}%`, width: s, height: s, animationDelay: `${d}s` }}
                />
              ))}
              <div className="gt-fish">
                <Fish />
              </div>
            </div>
          )}
          <div className="gbody">
            <div className="row between">
              <b style={{ fontSize: 17 }}>Bể bong bóng của Cá</b>
              <Chip tone="good">chạy không cần mạng</Chip>
            </div>
            <p className="muted" style={{ fontSize: 13.5 }}>
              Chạm vào bong bóng để bể chúng, thả lỏng đầu óc vài phút rồi quay lại bài.
            </p>
            {playing && allowed && (
              <p role="status" className="faint" style={{ fontSize: 12.5, fontWeight: 700 }}>
                Đã bể {score} bong bóng · còn {mmss(left)} giờ nghỉ
              </p>
            )}
            <div className="row">
              <Button
                size="sm"
                disabled={!allowed}
                onClick={() => {
                  setPlaying(!playing);
                }}
              >
                {playing ? "■ Dừng chơi" : "▶ Chơi"}
              </Button>
            </div>
          </div>
        </article>
      </div>
      <div className="grid g-7-5">
        <Card>
          <h3 style={{ marginBottom: 8 }}>Luật chơi lành mạnh</h3>
          <div className="list-row">
            <span>⏱</span>
            <span className="grow">Chỉ mở trong giờ nghỉ Pomodoro, sau mỗi hiệp tập trung.</span>
          </div>
          <div className="list-row">
            <span>🔒</span>
            <span className="grow">
              Hết giờ nghỉ, game tự khoá; Cá nhắc quay lại đúng bài đang làm.
            </span>
          </div>
          <div className="list-row">
            <span>📊</span>
            <span className="grow">Giờ chơi không tính vào giờ học và không cộng bong bóng.</span>
          </div>
        </Card>
        <Card>
          <h3 style={{ marginBottom: 6 }}>Hôm nay</h3>
          <div className="kpis" style={{ gridTemplateColumns: "repeat(2,minmax(0,1fr))" }}>
            <Kpi flip value={stats.plays} label="lần chơi" />
            <Kpi flip value={`${stats.secs} giây`} label="không tính giờ học" />
          </div>
          <p className="faint" style={{ fontSize: 12.5, fontWeight: 600, marginTop: 10 }}>
            Kỷ lục Bể bong bóng: {stats.best} 🫧 · lưu trên máy này
          </p>
        </Card>
      </div>
    </>
  );
}
